/**
 * Admin — Beta Feedback  ·  /admin/beta-feedback
 *
 * Wave 5-Late propagation (from BetaCodes v2 anchor).
 *   • AdminLayout wrap.
 *   • Section chrome with hairline dividers.
 *   • Status-count tiles in an admin-tone grid — no pastel palette.
 *   • Table on `border-collapse: collapse` + inline `border-top` per row.
 *   • Row density `py-1.5`.
 *   • Status pills via DS `Badge` with muted admin tones.
 *   • Category icons on `--ds-color-ink-muted` (no more #FCA5A5 pastels).
 *   • Detail modal in the same card chrome; inputs on `--ds-radius-input`
 *     (3px) + `--ds-color-input-border`, `px-2.5 py-1.5`.
 *
 * Business logic + testids + API surface preserved BYTE-EXACT.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Bug,
  Download,
  HelpCircle,
  Image as ImageIcon,
  Inbox,
  Lightbulb,
  Loader2,
  MessageSquare,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { api, formatApiError } from "@/api/client";
import { Heading } from "@/design-system/components/Heading";
import { Badge } from "@/design-system/components/Badge";
import { AccentButton, GhostButton, Section, SelectInput, TextInput } from "@/admin/_primitives";

const STATUSES = ["new", "triaged", "in_progress", "resolved", "wontfix"];
const SEVERITIES = ["low", "medium", "high", "critical"];
const CATEGORIES = ["bug", "suggestion", "confused", "other"];

const CAT_ICON = {
  bug: Bug,
  suggestion: Lightbulb,
  confused: HelpCircle,
  other: MessageSquare,
};

/**
 * Map the 5 status + 4 severity labels onto muted admin Badge tones.
 * Consumer-warm pastels (#FCA5A5, #FCD34D, #86EFAC…) are banned at admin
 * density — every mapping resolves to a tone from the executive palette.
 */
const STATUS_TONE = {
  new: "info",
  triaged: "warning",
  in_progress: "accent",
  resolved: "successMuted",
  wontfix: "neutralMuted",
};
const SEVERITY_TONE = {
  low: "neutralMuted",
  medium: "info",
  high: "warning",
  critical: "danger",
};

/* ------------------------------------------------------------------ */
/* Local admin primitives (mirror BetaCodes v2 anchor).                 */
/* ------------------------------------------------------------------ */


function Field({ label, children }) {
  return (
    <label className="block">
      <span
        className="block text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
        style={{ color: "var(--ds-color-ink-subtle)" }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}





/* ------------------------------------------------------------------ */
/* Root                                                                 */
/* ------------------------------------------------------------------ */

export default function BetaFeedbackAdmin() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (q) params.q = q;
      const { data } = await api.get("/admin/beta-feedback", { params });
      setItems(data.items || []);
      setCounts(data.status_counts || {});
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusFilter, categoryFilter]);

  function exportCsv() {
    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);
    if (categoryFilter) params.set("category", categoryFilter);
    const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/beta-feedback/export.csv?${params.toString()}`;
    window.open(url, "_blank", "noopener");
  }

  const headerActions = (
    <>
      <GhostButton onClick={load} data-testid="admin-beta-feedback-refresh">
        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        Refresh
      </GhostButton>
      <GhostButton onClick={exportCsv} data-testid="admin-beta-feedback-export">
        <Download className="w-3.5 h-3.5" />
        Export CSV
      </GhostButton>
    </>
  );

  return (
    <AdminLayout
      title="Beta Feedback"
      subtitle="Bugs, suggestions, and confusion submitted through the in-app widget."
      actions={headerActions}
    >
      <div data-testid="admin-beta-feedback-page">
        <div className="mb-4">
          <AdminPageHelp
            title="Early Access Feedback"
            purpose="Review qualitative feedback submitted by early access users through the in-app feedback widget."
            configures="This view is read-only. You can filter, sort, mark as reviewed, or archive individual entries."
            impact="Marking as reviewed updates the tracking state — it does not notify the user."
            safety={<span>Archiving is reversible via the &ldquo;Show archived&rdquo; toggle.</span>}
          />
        </div>

        {/* Status counts tile row */}
        <div
          className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4"
          data-testid="admin-beta-feedback-counts"
        >
          {STATUSES.map((s) => {
            const active = statusFilter === s;
            return (
              <button
                key={s}
                type="button"
                data-testid={`admin-beta-feedback-count-${s}`}
                onClick={() => setStatusFilter(active ? "" : s)}
                className="text-left rounded-[var(--ds-radius-card)] px-4 py-3 transition-colors"
                style={{
                  background: active
                    ? "var(--ds-color-accent-soft)"
                    : "var(--ds-color-surface)",
                  border: `1px solid ${
                    active ? "var(--ds-color-accent)" : "var(--ds-color-admin-border)"
                  }`,
                  boxShadow: "0 1px 0 0 var(--ds-color-admin-border)",
                  transitionDuration: "var(--ds-duration-fast)",
                }}
              >
                <div
                  className="text-[10px] uppercase tracking-[0.18em] font-semibold"
                  style={{
                    color: active
                      ? "var(--ds-color-accent-strong)"
                      : "var(--ds-color-ink-subtle)",
                  }}
                >
                  {s.replace("_", " ")}
                </div>
                <div
                  className="mt-1 tabular-nums"
                  style={{
                    fontFamily: "var(--ds-font-display)",
                    fontSize: "24px",
                    lineHeight: 1.1,
                    color: "var(--ds-color-ink)",
                    letterSpacing: "-0.01em",
                  }}
                >
                  {counts[s] || 0}
                </div>
              </button>
            );
          })}
        </div>

        <Section title="Filter feedback" icon={Search} testid="admin-beta-feedback-filters">
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Category">
              <SelectInput
                data-testid="admin-beta-feedback-category-filter"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                aria-label="Filter by category"
              >
                <option value="">All categories</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </SelectInput>
            </Field>
            <div className="flex-1 min-w-[220px]">
              <Field label="Search">
                <TextInput
                  data-testid="admin-beta-feedback-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && load()}
                  placeholder="Search message, email, page…"
                />
              </Field>
            </div>
          </div>
        </Section>

        <Section
          title="Submissions"
          icon={Inbox}
          badge={
            <span
              className="text-[11px] uppercase tracking-[0.14em]"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              {total} total
            </span>
          }
          testid="admin-beta-feedback-table-section"
          bodyClassName="p-0"
        >
          <div className="overflow-x-auto">
            <table
              className="w-full text-[13px]"
              style={{ borderCollapse: "collapse" }}
              data-testid="admin-beta-feedback-table"
            >
              <thead>
                <tr
                  className="text-left text-[10px] uppercase tracking-[0.16em] font-semibold"
                  style={{
                    color: "var(--ds-color-ink-subtle)",
                    background: "var(--ds-color-admin-surface-muted)",
                    borderBottom: "1px solid var(--ds-color-admin-border)",
                  }}
                >
                  <th className="px-4 py-2">When</th>
                  <th className="px-4 py-2">Category</th>
                  <th className="px-4 py-2">Sev</th>
                  <th className="px-4 py-2">User</th>
                  <th className="px-4 py-2">Message</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-8 text-center"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1.5" />
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-10 text-center"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      No feedback yet.
                    </td>
                  </tr>
                )}
                {items.map((row) => {
                  const CatI = CAT_ICON[row.category] || CAT_ICON.other;
                  return (
                    <tr
                      key={row.id}
                      className="cursor-pointer"
                      style={{ borderTop: "1px solid var(--ds-color-admin-border)" }}
                      onClick={() => setSelected(row)}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "var(--ds-color-admin-surface-muted)")
                      }
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      data-testid={`admin-beta-feedback-row-${row.id}`}
                    >
                      <td
                        className="px-4 py-1.5 text-[12px]"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {new Date(row.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-1.5">
                        <span
                          className="inline-flex items-center gap-1.5 text-[12px] capitalize"
                          style={{ color: "var(--ds-color-ink-muted)" }}
                        >
                          <CatI className="w-3.5 h-3.5" aria-hidden="true" /> {row.category}
                        </span>
                      </td>
                      <td className="px-4 py-1.5">
                        <Badge
                          tone={SEVERITY_TONE[row.severity] || "neutralMuted"}
                          size="sm"
                          className="uppercase tracking-[0.14em]"
                        >
                          {row.severity}
                        </Badge>
                      </td>
                      <td
                        className="px-4 py-1.5 font-mono text-[12px]"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.user_email_snapshot || "anon"}
                      </td>
                      <td
                        className="px-4 py-1.5 truncate max-w-[300px]"
                        style={{ color: "var(--ds-color-ink)" }}
                      >
                        {(row.message || "").slice(0, 120)}
                      </td>
                      <td className="px-4 py-1.5">
                        <Badge
                          tone={STATUS_TONE[row.status] || "neutralMuted"}
                          size="sm"
                          className="uppercase tracking-[0.14em]"
                        >
                          {row.status}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      </div>

      {selected && (
        <FeedbackDetail
          row={selected}
          onClose={() => setSelected(null)}
          onUpdated={() => {
            setSelected(null);
            load();
          }}
        />
      )}
    </AdminLayout>
  );
}

/* ------------------------------------------------------------------ */
/* FeedbackDetail modal                                                 */
/* ------------------------------------------------------------------ */

function FeedbackDetail({ row, onClose, onUpdated }) {
  const [status, setStatus] = useState(row.status);
  const [notes, setNotes] = useState(row.admin_notes || "");
  const [severity, setSeverity] = useState(row.severity);
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api.patch(`/admin/beta-feedback/${row.id}`, {
        status,
        admin_notes: notes,
        severity,
      });
      toast.success("Updated.");
      onUpdated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(15,18,24,0.72)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-[var(--ds-radius-card)] max-h-[90vh] overflow-hidden flex flex-col"
        style={{
          background: "var(--ds-color-surface)",
          border: "1px solid var(--ds-color-admin-border)",
          boxShadow:
            "0 12px 32px -12px rgba(15,18,24,0.28), 0 1px 0 0 var(--ds-color-admin-border)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-start justify-between gap-3 px-4 py-3.5"
          style={{ borderBottom: "1px solid var(--ds-color-admin-border)" }}
        >
          <div className="min-w-0">
            <div
              className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-1"
              style={{ color: "var(--ds-color-ink-subtle)" }}
            >
              {row.category} · {row.severity}
            </div>
            <Heading level={3} size="sm" weight="semibold" className="leading-tight">
              Feedback from {row.user_email_snapshot || "anonymous"}
            </Heading>
            <div
              className="text-[11px] mt-1"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              {new Date(row.created_at).toLocaleString()} · {row.page_url || "no page"}
            </div>
          </div>
          <button
            type="button"
            data-testid="admin-beta-feedback-detail-close"
            onClick={onClose}
            className="text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] shrink-0"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto">
          <div
            className="rounded-[var(--ds-radius-input)] border p-3 whitespace-pre-wrap text-[13px]"
            style={{
              borderColor: "var(--ds-color-admin-border)",
              background: "var(--ds-color-admin-surface-muted)",
              color: "var(--ds-color-ink)",
            }}
            data-testid="admin-beta-feedback-detail-message"
          >
            {row.message}
          </div>

          {row.screenshot_url && (
            <div>
              <div
                className="text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
                style={{ color: "var(--ds-color-ink-subtle)" }}
              >
                Screenshot
              </div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL || ""}${row.screenshot_url}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-[12px] hover:underline"
                style={{ color: "var(--ds-color-accent-strong)" }}
              >
                <ImageIcon className="w-3.5 h-3.5" /> Open in new tab
              </a>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Status">
              <SelectInput
                data-testid="admin-beta-feedback-detail-status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </SelectInput>
            </Field>
            <Field label="Severity">
              <SelectInput
                data-testid="admin-beta-feedback-detail-severity"
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                className="w-full"
              >
                {SEVERITIES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </SelectInput>
            </Field>
          </div>

          <Field label="Admin notes">
            <textarea
              data-testid="admin-beta-feedback-detail-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full rounded-[var(--ds-radius-input)] border bg-[var(--ds-color-surface)] px-2.5 py-1.5 text-[13px] text-[var(--ds-color-ink)] focus:outline-none focus:border-[var(--ds-color-input-border-strong)]"
              style={{ borderColor: "var(--ds-color-input-border)" }}
            />
          </Field>
        </div>

        <div
          className="flex items-center justify-end gap-2 px-4 py-3"
          style={{ borderTop: "1px solid var(--ds-color-admin-border)" }}
        >
          <GhostButton onClick={onClose}>Close</GhostButton>
          <AccentButton
            data-testid="admin-beta-feedback-detail-save"
            onClick={save}
            disabled={saving}
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Save changes
          </AccentButton>
        </div>
      </div>
    </div>
  );
}
